import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { imgUrl } from "../lib/api";
import { useCart, FREE_SHIPPING } from "../context/CartContext";
import { useLang } from "../i18n";

export default function CartDrawer() {
  const { items, setQty, removeItem, subtotal, shipping, drawerOpen, setDrawerOpen } = useCart();
  const { t, lang } = useLang();
  const remaining = Math.max(0, FREE_SHIPPING - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING) * 100);

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)} className="fixed inset-0 bg-bark/60 backdrop-blur-sm z-50" data-testid="cart-overlay" />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-parchment z-50 flex flex-col shadow-2xl" data-testid="cart-drawer">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5D9C8]">
              <h2 className="font-serif text-2xl font-semibold" data-testid="cart-drawer-title">{t("cart.title")}</h2>
              <button onClick={() => setDrawerOpen(false)} data-testid="cart-close-btn" className="p-2 hover:text-honey transition-colors"><X size={20} /></button>
            </div>
            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6" data-testid="cart-empty-state">
                <p className="text-cocoa/60">{t("cart.empty")}</p>
                <Link to="/shop" onClick={() => setDrawerOpen(false)} data-testid="cart-empty-cta"
                  className="text-honey font-medium underline underline-offset-4">{t("cart.empty.cta")}</Link>
              </div>
            ) : (
              <>
                <div className="px-6 py-4 border-b border-[#E5D9C8] bg-cream/60">
                  <p className="text-xs font-mono text-cocoa/70 mb-2" data-testid="free-shipping-message">
                    {remaining > 0 ? t("cart.free.progress").replace("{x}", `€${remaining.toFixed(2)}`) : t("cart.free.done")}
                  </p>
                  <div className="h-1.5 bg-[#E5D9C8] rounded-full overflow-hidden">
                    <motion.div className="h-full bg-honey" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} data-testid="free-shipping-bar" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                  {items.map((i) => (
                    <motion.div key={i.key} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex gap-4" data-testid={`cart-item-${i.key}`}>
                      <img src={imgUrl(i.image)} alt={i.name_it} className="w-20 h-20 object-cover rounded-sm border border-[#E5D9C8]" />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif font-semibold leading-tight">{lang === "it" ? i.name_it : i.name_en}</p>
                        <p className="text-xs font-mono text-cocoa/50 mt-0.5">{i.size}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => setQty(i.key, i.qty - 1)} data-testid={`cart-minus-${i.key}`} className="p-1 border border-[#E5D9C8] hover:border-honey transition-colors"><Minus size={13} /></button>
                          <span className="text-sm font-mono w-5 text-center" data-testid={`cart-qty-${i.key}`}>{i.qty}</span>
                          <button onClick={() => setQty(i.key, i.qty + 1)} data-testid={`cart-plus-${i.key}`} className="p-1 border border-[#E5D9C8] hover:border-honey transition-colors"><Plus size={13} /></button>
                        </div>
                      </div>
                      <div className="text-right flex flex-col justify-between items-end">
                        <p className="font-mono text-sm font-semibold" data-testid={`cart-price-${i.key}`}>€{(i.price * i.qty).toFixed(2)}</p>
                        <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.key}`} className="text-cocoa/40 hover:text-terra transition-colors"><Trash2 size={15} /></button>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className="border-t border-[#E5D9C8] px-6 py-5 space-y-2 bg-white/60">
                  <div className="flex justify-between text-sm text-cocoa/70"><span>{t("cart.subtotal")}</span><span className="font-mono" data-testid="cart-subtotal">€{subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm text-cocoa/70"><span>{t("cart.shipping")}</span><span className="font-mono" data-testid="cart-shipping">{shipping === 0 ? t("cart.free") : `€${shipping.toFixed(2)}`}</span></div>
                  <div className="flex justify-between font-serif text-xl font-semibold pt-2 border-t border-[#E5D9C8]"><span>{t("cart.total")}</span><span className="font-mono" data-testid="cart-total">€{(subtotal + shipping).toFixed(2)}</span></div>
                  <Link to="/checkout" onClick={() => setDrawerOpen(false)} data-testid="cart-checkout-btn"
                    className="mt-3 flex items-center justify-center gap-2 w-full bg-bark text-parchment py-3.5 font-medium hover:bg-honey transition-colors duration-300">
                    {t("cart.checkout")} <ArrowRight size={16} />
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
